import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getServerConfig } from "../config.server";
import { requirePermission } from "../auth/session.server";
import { generateUniqueSlug } from "../utils/slug.server";
import { getDb, schema } from "../db/client.server";
import type { ArticleBlock } from "../types/content";
import { articleBlocksToDoc } from "../doc/doc-convert";

export const BLOCK_TEMPLATES: { name: string; blocks: ArticleBlock[] }[] = [
  {
    name: "Article intro + sections",
    blocks: [
      { type: "p", text: "Opening paragraph introducing the topic." },
      { type: "h2", text: "Key considerations" },
      { type: "p", text: "Supporting detail for the first section." },
      { type: "ul", items: ["First point", "Second point", "Third point"] },
      { type: "h2", text: "Conclusion" },
      { type: "p", text: "Closing summary and practical takeaway." },
    ],
  },
  {
    name: "Tax update format",
    blocks: [
      { type: "p", text: "Brief context on the regulatory change." },
      { type: "h2", text: "What changed" },
      { type: "p", text: "Explanation of the new rules." },
      { type: "h3", text: "Who is affected" },
      { type: "ul", items: ["Affected group one", "Affected group two"] },
      { type: "h2", text: "Next steps" },
      { type: "p", text: "Recommended actions for readers." },
    ],
  },
  {
    name: "Audit advisory format",
    blocks: [
      { type: "p", text: "Overview of the audit topic." },
      { type: "h2", text: "Thresholds and eligibility" },
      { type: "p", text: "Criteria explanation." },
      { type: "h2", text: "Benefits and risks" },
      { type: "ul", items: ["Benefit one", "Risk one"] },
    ],
  },
];

export const getBlockTemplates = createServerFn({ method: "GET" }).handler(async () => {
  await requirePermission("insights", "read");
  return BLOCK_TEMPLATES;
});

export const generateInsightDraft = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      topic: z.string().min(1),
      category: z.string().optional(),
      outline: z.array(z.string()).optional(),
    }),
  )
  .handler(async ({ data }) => {
    await requirePermission("insights", "write");
    const { openaiApiKey } = getServerConfig();

    let blocks: ArticleBlock[] = BLOCK_TEMPLATES[0].blocks;
    let title = data.topic;
    let excerpt = `An overview of ${data.topic}.`;
    let suggestedSlug = await generateUniqueSlug(schema.insights, data.topic);

    if (openaiApiKey) {
      try {
        const res = await fetch("https://api.openai.com/v1/chat/completions", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${openaiApiKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "gpt-4o-mini",
            messages: [
              {
                role: "system",
                content:
                  "You are a professional tax and audit content writer for KS Lau & Co, a Malaysian chartered accountancy firm. Return JSON only with keys: title, excerpt, blocks (array of {type,text} or {type,items} for ul). Block types: p, h2, h3, ul.",
              },
              {
                role: "user",
                content: `Topic: ${data.topic}\nCategory: ${data.category ?? "Tax"}\nOutline: ${(data.outline ?? []).join("; ")}`,
              },
            ],
            response_format: { type: "json_object" },
          }),
        });
        if (res.ok) {
          const json = (await res.json()) as { choices: { message: { content: string } }[] };
          const parsed = JSON.parse(json.choices[0].message.content) as {
            title?: string;
            excerpt?: string;
            blocks?: ArticleBlock[];
          };
          if (parsed.title) title = parsed.title;
          if (parsed.excerpt) excerpt = parsed.excerpt;
          if (parsed.blocks?.length) blocks = parsed.blocks;
          else blocks = BLOCK_TEMPLATES[0].blocks;
          suggestedSlug = await generateUniqueSlug(schema.insights, title);
        } else {
          blocks = buildFallbackBlocks(data);
        }
      } catch {
        blocks = buildFallbackBlocks(data);
      }
    } else {
      blocks = buildFallbackBlocks(data);
    }

    return {
      title,
      excerpt,
      blocks,
      editorDoc: articleBlocksToDoc(blocks),
      suggestedSlug,
      category: data.category ?? "Tax",
      status: "draft" as const,
    };
  });

function buildFallbackBlocks(data: { topic: string; outline?: string[] }): ArticleBlock[] {
  const blocks: ArticleBlock[] = [{ type: "p", text: `This article explores ${data.topic} and its implications for businesses in Malaysia.` }];
  for (const point of data.outline ?? ["Background", "Key points", "Practical steps"]) {
    blocks.push({ type: "h2", text: point });
    blocks.push({ type: "p", text: `Details on ${point.toLowerCase()} related to ${data.topic}.` });
  }
  blocks.push({ type: "h2", text: "Conclusion" });
  blocks.push({ type: "p", text: "Summary and recommended next steps for readers." });
  return blocks;
}

export const createDraftFromTemplate = createServerFn({ method: "POST" })
  .inputValidator(z.object({ templateIndex: z.number(), title: z.string().min(1) }))
  .handler(async ({ data }) => {
    await requirePermission("insights", "write");
    const template = BLOCK_TEMPLATES[data.templateIndex];
    if (!template) throw new Error("Template not found");
    const slug = await generateUniqueSlug(schema.insights, data.title);
    return {
      title: data.title,
      slug,
      contentBlocks: template.blocks,
      editorDoc: articleBlocksToDoc(template.blocks),
      contentFormat: "blocks" as const,
      status: "draft" as const,
    };
  });
